/**
 * Flags an operator CAN reasonably change, but most never need to — page
 * deletion, the overlay and watermark tools, v2 folders, the transcription
 * engine map.
 *
 * EXPECTED IN EVERY ENVIRONMENT, exactly like `standard.ts` — an absent one is
 * reported Missing and offered an Add. That is the whole line between this file
 * and `developer.ts`: a developer key is one we do NOT need registered
 * everywhere. These we do; they simply are not what anyone opens the page for,
 * so they get their own tab and stay out of All.
 */

import { ManifestDefinition } from '@redact-modules/admin/models';
export const ADVANCED_DEFINITIONS: readonly ManifestDefinition[] = [
  {
    configKey: 'enableTextOverlay',
    configType: 'Boolean',
    configLevel: 'Organization',
    required: false,
    secured: false,
    description: 'Feature Flag: On-screen text overlay tool (default on)',
    defaultValue: 'true',
  },
  {
    configKey: 'enableDeleteNonEmptyFolder',
    configType: 'Boolean',
    configLevel: 'Organization',
    required: false,
    secured: false,
    description:
      'Feature Flag: Allow deleting folders/requests that still contain files',
    defaultValue: 'false',
  },
  {
    configKey: 'v2FoldersEnabled',
    configType: 'Boolean',
    configLevel: 'Organization',
    required: false,
    secured: false,
    description: 'Feature Flag: Use v2 folders',
    defaultValue: 'false',
  },
  {
    /**
     * A map of language code to engine id, and the newer half of the pair:
     * job creation picks `transcriptionEngines?.[language] ?? transcriptionEngineId`,
     * so this supersedes the single id above wherever a language has an entry.
     *
     * `"{}"` is a SAFE default, checked rather than assumed. An empty map
     * behaves exactly as an absent one: every consumer guards with
     * `Object.keys(transcriptionEngines).length > 0` or falls back through
     * `?.[language] ?? transcriptionEngineId`, and the key is NOT in the
     * `pick(config, [...])` list in engines/selectors.ts — so unlike the engine
     * ids, there is no `window.config` value for a default to override.
     */
    configKey: 'transcriptionEngines',
    configType: 'JSON',
    configLevel: 'Organization',
    required: false,
    secured: false,
    description: 'Map of language to engine id.',
    defaultValue: '{}',
  },
  {
    /**
     * JSON-typed and declares no default; the app supplies its own options.
     */
    configKey: 'transcriptionEngineOptions',
    configType: 'JSON',
    configLevel: 'Organization',
    required: false,
    secured: false,
    description: 'Redact: Transcription Engine Options',
  },
  {
    configKey: 'rootFolderSmartSelection',
    configType: 'Boolean',
    configLevel: 'Organization',
    required: false,
    secured: false,
    description:
      'Feature Flag: when several owner-less root folders are returned, resolve the org root by child-folder count instead of taking the first',
    defaultValue: 'false',
  },
  {
    /**
     * THIS DEFAULT OUTRANKS `window.config`, which is unusual and worth knowing
     * before changing it.
     *
     * `engines/selectors.ts` reads the engine id straight off `window.config`
     * via `pick(config, [...])`, and merges any application-config value OVER
     * that. So this value decides transcription for every organization that has
     * not set its own — in every environment where the definition is
     * registered — regardless of what that environment's `config_*.js` says.
     *
     * The two ids in circulation disagree: every `config_*.js` and
     * `static/config.js` in this repo ships
     * `e97d1564-39ff-4016-a034-e1f32aa9eb7d`, while this one is the id
     * docs/ApplicationConfig.md has always registered. Registering this as the
     * default therefore REPOINTS transcription anywhere the config file is the
     * one being followed today. That is a behaviour change, made deliberately;
     * it carried no default before precisely to avoid making it by accident.
     */
    configKey: 'transcriptionEngineId',
    configType: 'String',
    configLevel: 'Organization',
    required: false,
    secured: false,
    description: 'Redact: Engine Id to use for Transcription',
    defaultValue: '01e54769-c448-4ece-a500-4fab5b49b722',
  },
  {
    configKey: 'useGPU',
    configType: 'Boolean',
    configLevel: 'Organization',
    required: false,
    secured: false,
    description: 'Feature Flag: Use GPU detection engines',
    defaultValue: 'false',
  },
];
